import React, { useEffect, useState, useMemo } from 'react'
import { Flex, Spin } from 'antd'

import { PageLayout } from '@/shared/ui/layout'
import { AccountSelectionCard } from './components/AccountSelectionCard'
import { TransferModeCard } from './components/TransferModeCard'
import { DataTransferCard } from './components/DataTransferCard'
import { TransferKey } from 'antd/es/transfer/interface'
import { Account } from '@/widgets/accountsGrid'
import { getSourceApi } from '@/shared/api/config'
import { useAuthStore } from '@/app/store/authStore'
import { loadSourceData } from '@/shared/api/sourceApi'
import { useTransferDataForTransfer } from '../model/useTransferDataForTransfer'
import { useAgentsInfoQuery } from '../model/useAgentsInfoQuery'
import { useAgentsQuery } from '@/shared/api/useAgentsQuery'
import { useSystemUrl } from '../model/useSystemUrl'
import { useStartTransfer } from '../model/useStartTransfer'
import {
  SourceAccount,
  SourceDriver,
  SourceGeozone,
  SourceRetranslation,
  SourceUser,
  SourceVehicle,
} from '@/app/types/source'
import {
  DEFAULT_MESSAGE_PERIOD,
  TRANSFER_MODES,
  TransferMode,
} from '../model/transferTypes'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'
import { getSystemDisplayName } from '@/shared/lib/getSystemDisplayName'
import { useOmnicommStore } from '@/app/store/omnicommStore'
import { useAxentaStore } from '@/app/store/axentaStore'
import { useFortMonitorStore } from '@/app/store/fortmonitorStore'
import { TransferButtonSection } from './components/TransferButtonSetion/TransferButtonSection'
import { SectionCard } from '@/shared/ui/layout/styled'

export const StandardTransferPage: React.FC = () => {
  const { user, userPassword, setTokenAccount, currentServer } = useAuthStore()
  const { credentials: omnicommCredentialsData } = useOmnicommStore()
  const { credentials: axentaCredentialsData } = useAxentaStore()
  const { credentials: fortMonitorCredentialsData } = useFortMonitorStore()
  const { selectedSystem } = useSystemStore()
  const isOmnicomm = selectedSystem === SYSTEM_TYPE.OMNICOMM
  const isAxenta = selectedSystem === SYSTEM_TYPE.AXENTA
  const isFortMonitor = selectedSystem === SYSTEM_TYPE.FORTMONITOR
  const [selectedSubpartner, setSelectedSubpartner] = useState('')
  const [userSelectedSubpartner, setUserSelectedSubpartner] = useState(false)
  const [isMyAccountSelected, setIsMyAccountSelected] = useState(false)
  const { systemUrl } = useSystemUrl()
  const [selectedAccount, setSelectedAccount] = useState('')
  const [transferMode, setTransferMode] = useState<TransferMode>(
    TRANSFER_MODES.ENTITIES
  )
  const [objectsTargetKeys, setObjectsTargetKeys] = useState<TransferKey[]>([])
  const [usersTargetKeys, setUsersTargetKeys] = useState<TransferKey[]>([])
  const [loadMessages, setLoadMessages] = useState(false)
  const [loadDriversCodes, setLoadDriversCodes] = useState(false)

  const [retranslationsData, setRetranslationsData] = useState<any[]>([])
  const [retranslationsTargetKeys, setRetranslationsTargetKeys] = useState<
    TransferKey[]
  >([])

  const [accountsTargetKeys, setAccountsTargetKeys] = useState<TransferKey[]>(
    []
  )

  const [geozonesTargetKeys, setGeozonesTargetKeys] = useState<TransferKey[]>(
    []
  )
  const [driversTargetKeys, setDriversTargetKeys] = useState<TransferKey[]>([])

  const [loadMessagesFull, setLoadMessagesFull] = useState(false)
  const [messagePeriod, setMessagePeriod] = useState(DEFAULT_MESSAGE_PERIOD)
  const [messagePeriodFull, setMessagePeriodFull] = useState(
    DEFAULT_MESSAGE_PERIOD
  )

  const [targetAgentAccounts, setTargetAgentAccounts] = useState<Account[]>([])

  const sourceApiUrl = getSourceApi(selectedSystem)

  const omnicommCredentials = useMemo(() => {
    if (isOmnicomm && omnicommCredentialsData) {
      return omnicommCredentialsData
    }
    return null
  }, [isOmnicomm, omnicommCredentialsData])

  const omnicommAuthParam = useMemo(() => {
    if (isOmnicomm && omnicommCredentials) {
      return JSON.stringify(omnicommCredentials)
    }
    return null
  }, [isOmnicomm, omnicommCredentials])

  const axentaCredentials = useMemo(() => {
    if (isAxenta && axentaCredentialsData) {
      return axentaCredentialsData
    }
    return null
  }, [isAxenta, axentaCredentialsData])

  const axentaAuthParam = useMemo(() => {
    if (isAxenta && axentaCredentials) {
      return JSON.stringify({
        login: axentaCredentials.login,
        password: axentaCredentials.password,
      })
    }
    return null
  }, [isAxenta, axentaCredentials])

  const fortMonitorCredentials = useMemo(() => {
    if (isFortMonitor && fortMonitorCredentialsData) {
      return fortMonitorCredentialsData
    }
    return null
  }, [isFortMonitor, fortMonitorCredentialsData])

  const fortMonitorAuthParam = useMemo(() => {
    if (isFortMonitor && fortMonitorCredentials) {
      return JSON.stringify(fortMonitorCredentials)
    }
    return null
  }, [isFortMonitor, fortMonitorCredentials])

  const targetAgentsInfoQuery = useAgentsInfoQuery({
    username: user?.username,
    userPassword,
    currentServer,
  })

  useEffect(() => {
    if (targetAgentsInfoQuery.data)
      setTargetAgentAccounts(targetAgentsInfoQuery.data)
  }, [targetAgentsInfoQuery.data])

  const activeCredentials =
    omnicommCredentials || axentaCredentials || fortMonitorCredentials

  const agentsQuery = useAgentsQuery({
    systemType: selectedSystem as SYSTEM_TYPE,
    credentials: activeCredentials,
    sourceApiUrl,
    systemUrl,
  })

  useEffect(() => {
    const data = agentsQuery.data?.data
    if (!data) return
    setAccountsData(data)
    if (data.length > 0) {
      const mainId = data[0].id
      setSubPartners(
        data.filter((account: SourceAccount) => account.id !== mainId)
      )
      const defaultAccountName = isOmnicomm ? 'Omnicomm User' : 'Axenta User'
      setTokenAccount(data[0].name || defaultAccountName)
    }
  }, [agentsQuery.data, isOmnicomm, isAxenta])

  const handleGeozonesChange = (newTargetKeys: TransferKey[]) => {
    setGeozonesTargetKeys(newTargetKeys)
  }

  const handleDriversChange = (newTargetKeys: TransferKey[]) => {
    setDriversTargetKeys(newTargetKeys)
  }

  const handleSelectAllGeozones = () => {
    const dataToUse = geozonesDataForTransfer
    if (dataToUse.length > 0) {
      setGeozonesTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllGeozones = () => {
    setGeozonesTargetKeys([])
  }

  const handleSelectAllDrivers = () => {
    const dataToUse = driversDataForTransfer
    if (dataToUse.length > 0) {
      setDriversTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllDrivers = () => {
    setDriversTargetKeys([])
  }

  const handleAccountsChange = (newTargetKeys: TransferKey[]) => {
    setAccountsTargetKeys(newTargetKeys)
  }

  const handleSelectAllAccounts = () => {
    const dataToUse = accountsDataForTransfer
    if (dataToUse.length > 0) {
      setAccountsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllAccounts = () => {
    setAccountsTargetKeys([])
  }

  const [vehiclesData, setVehiclesData] = useState<any[]>([])
  const [driversData, setDriversData] = useState<any[]>([])
  const [usersData, setUsersData] = useState<any[]>([])
  const [geosData, setGeosData] = useState<any[]>([])
  const [accountsData, setAccountsData] = useState<any[]>([])
  const [subPartners, setSubPartners] = useState<any[]>([])

  const {
    objectsDataForTransfer,
    usersDataForTransfer,
    geozonesDataForTransfer,
    driversDataForTransfer,
    accountsDataForTransfer,
    retranslationsDataForTransfer,
  } = useTransferDataForTransfer({
    vehiclesData,
    driversData,
    usersData,
    geosData,
    accountsData,
    retranslationsData,
    selectedSubpartner,
    userSelectedSubpartner,
    transferMode,
    shouldFilterByParentId: !isFortMonitor,
  })

  const loadDataFromApi = async (token: string) => {
    if (!token && !isOmnicomm && !isAxenta && !isFortMonitor) return

    setVehiclesData([])
    setDriversData([])
    setUsersData([])
    setGeosData([])
    setAccountsData([])
    setSubPartners([])
    setUserSelectedSubpartner(false)
    setSelectedSubpartner('')

    try {
      let authParam = token

      if (isOmnicomm) {
        if (omnicommCredentials) {
          authParam = JSON.stringify(omnicommCredentials)
        } else {
          console.warn('Omnicomm credentials not found')
          return
        }
      }
      if (isAxenta) {
        if (axentaCredentials) {
          authParam = encodeURIComponent(JSON.stringify(axentaCredentials))
        }
      }
      if (isFortMonitor) {
        if (fortMonitorCredentials) {
          authParam = JSON.stringify(fortMonitorCredentials)
        } else {
          console.warn('FortMonitor credentials not found')
          return
        }
      }

      if (authParam) {
        Promise.all([
          loadSourceData<SourceVehicle>(
            'Vehicles',
            authParam,
            sourceApiUrl,
            systemUrl,
            selectedSystem
          ).then((response) => {
            setVehiclesData(response.data)
            return response
          }),
          loadSourceData<SourceDriver>(
            'Drivers',
            authParam,
            sourceApiUrl,
            systemUrl,
            selectedSystem
          ).then((response) => {
            setDriversData(response.data)
            return response
          }),
          loadSourceData<SourceUser>(
            'Users',
            authParam,
            sourceApiUrl,
            systemUrl,
            selectedSystem
          ).then((response) => {
            setUsersData(response.data)
            return response
          }),
        ]).catch((error) => {
          console.error('Ошибка при параллельной загрузке:', error)
        })

        const geozonesResponse = await loadSourceData<SourceGeozone>(
          'Geozones',
          authParam,
          sourceApiUrl,
          systemUrl,
          selectedSystem
        )
        setGeosData(geozonesResponse.data)
        const retranslationsResponse =
          await loadSourceData<SourceRetranslation>(
            'Retranslations',
            authParam,
            sourceApiUrl,
            systemUrl,
            selectedSystem
          )
        setRetranslationsData(retranslationsResponse.data)
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error)
    }
  }

  useEffect(() => {
    if (isOmnicomm) {
      if (omnicommCredentials) {
        loadDataFromApi('')
      }
    } else if (isAxenta) {
      if (axentaCredentials) {
        loadDataFromApi('')
      }
    } else if (isFortMonitor) {
      if (fortMonitorCredentials) {
        loadDataFromApi('')
      }
    } else {
      setVehiclesData([])
      setDriversData([])
      setUsersData([])
      setGeosData([])
    }
  }, [
    systemUrl,
    isOmnicomm,
    isAxenta,
    isFortMonitor,
    omnicommCredentials,
    axentaCredentials,
    fortMonitorCredentials,
  ])

  useEffect(() => {
    if (accountsData.length > 0 && !selectedSubpartner) {
      const mainAccountId = accountsData[0].id
      setSelectedSubpartner(mainAccountId)
      setUserSelectedSubpartner(true)
    }
  }, [accountsData, selectedSubpartner])

  useEffect(() => {
    setObjectsTargetKeys([])
    setUsersTargetKeys([])
    setGeozonesTargetKeys([])
    setDriversTargetKeys([])
    setRetranslationsTargetKeys([])
    if (transferMode === TRANSFER_MODES.FULL) {
      setAccountsTargetKeys([])
    }
  }, [selectedSubpartner, transferMode])

  const handleObjectsChange = (newTargetKeys: TransferKey[]) => {
    setObjectsTargetKeys(newTargetKeys)
  }

  const handleUsersChange = (newTargetKeys: TransferKey[]) => {
    setUsersTargetKeys(newTargetKeys)
  }

  const handleSelectAllObjects = () => {
    const dataToUse = objectsDataForTransfer
    if (dataToUse.length > 0) {
      setObjectsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllObjects = () => {
    setObjectsTargetKeys([])
  }

  const handleSelectAllUsers = () => {
    const dataToUse = usersDataForTransfer
    if (dataToUse.length > 0) {
      setUsersTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllUsers = () => {
    setUsersTargetKeys([])
  }

  const handleLoadMessagesChange = (checked: boolean) => {
    setLoadMessages(checked)
  }

  const handleMessagePeriodChange = (period: number) => {
    setMessagePeriod(period)
  }

  const handleLoadDriversCodes = (checked: boolean) => {
    setLoadDriversCodes(checked)
  }

  const handleLoadMessagesFullChange = (checked: boolean) => {
    setLoadMessagesFull(checked)
  }

  const handleMessagePeriodFullChange = (period: number) => {
    setMessagePeriodFull(period)
  }

  const handleRetranslationsChange = (newTargetKeys: TransferKey[]) => {
    setRetranslationsTargetKeys(newTargetKeys)
  }

  const handleSelectAllRetranslations = () => {
    const dataToUse = retranslationsDataForTransfer
    if (dataToUse.length > 0) {
      setRetranslationsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllRetranslations = () => {
    setRetranslationsTargetKeys([])
  }

  const buildSourceAuth = () => {
    if (isOmnicomm) return { auth: omnicommAuthParam }
    if (isAxenta) return { auth: axentaAuthParam }
    return { auth: fortMonitorAuthParam }
  }

  const { handleStartTransfer, isTransferring } = useStartTransfer({
    transferMode,
    isMyAccountSelected,
    selectedAccount,
    targetAgentAccounts,
    selectedSubpartner,
    accountsData,
    subPartners,
    accountsTargetKeys,
    usersTargetKeys,
    objectsTargetKeys,
    driversTargetKeys,
    geozonesTargetKeys,
    retranslationsTargetKeys,
    loadMessages,
    messagePeriod,
    loadMessagesFull,
    messagePeriodFull,
    loadDriversCodes,
    systemUrl,
    buildSourceAuth,
  })

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccount(accountId)
    setIsMyAccountSelected(false)
  }

  const handleMyAccountSelect = (checked: boolean) => {
    setIsMyAccountSelected(checked)
    if (checked) {
      setSelectedAccount('')
    }
  }

  const handleTransferModeChange = (mode: TransferMode) => {
    setTransferMode(mode)
  }

  const isAccountNotSelected = !selectedAccount && !isMyAccountSelected
  const isButtonDisabled = isAccountNotSelected || isTransferring

  return (
    <PageLayout
      title={`Перенос данных из ${getSystemDisplayName(selectedSystem)} в ГЛОНАССSoft`}
      subtitle="Настройте параметры для переноса объектов, пользователей и сообщений"
    >
      <Flex vertical gap={32}>
        <Spin spinning={isTransferring}>
          <AccountSelectionCard
            selectedAccount={selectedAccount}
            onAccountSelect={handleAccountSelect}
            isLoading={targetAgentsInfoQuery.isLoading}
            accounts={targetAgentAccounts}
            isMyAccountSelected={isMyAccountSelected}
            onMyAccountSelect={handleMyAccountSelect}
          />
        </Spin>

        <Spin spinning={isTransferring}>
          <TransferModeCard
            transferMode={transferMode}
            onTransferModeChange={handleTransferModeChange}
          />
        </Spin>

        <SectionCard>
          <DataTransferCard
            isLoading={false}
            transferMode={transferMode}
            // Для режима "Только сущности"
            objectsTargetKeys={objectsTargetKeys}
            usersTargetKeys={usersTargetKeys}
            geozonesTargetKeys={geozonesTargetKeys}
            driversTargetKeys={driversTargetKeys}
            loadMessages={loadMessages}
            loadDriversCodes={loadDriversCodes}
            messagePeriod={messagePeriod}
            retranslationsTargetKeys={retranslationsTargetKeys}
            // Для режима "Полный перенос"
            accountsTargetKeys={accountsTargetKeys}
            loadMessagesFull={loadMessagesFull}
            messagePeriodFull={messagePeriodFull}
            // Обработчики для режима "Только сущности"
            onObjectsChange={handleObjectsChange}
            onUsersChange={handleUsersChange}
            onGeozonesChange={handleGeozonesChange}
            onDriversChange={handleDriversChange}
            onSelectAllObjects={handleSelectAllObjects}
            onDeselectAllObjects={handleDeselectAllObjects}
            onSelectAllUsers={handleSelectAllUsers}
            onDeselectAllUsers={handleDeselectAllUsers}
            onSelectAllGeozones={handleSelectAllGeozones}
            onDeselectAllGeozones={handleDeselectAllGeozones}
            onSelectAllDrivers={handleSelectAllDrivers}
            onDeselectAllDrivers={handleDeselectAllDrivers}
            onLoadMessagesChange={handleLoadMessagesChange}
            onMessagePeriodChange={handleMessagePeriodChange}
            onLoadDriversCodesChange={handleLoadDriversCodes}
            onRetranslationsChange={handleRetranslationsChange}
            onSelectAllRetranslations={handleSelectAllRetranslations}
            onDeselectAllRetranslations={handleDeselectAllRetranslations}
            // Обработчики для режима "Полный перенос"
            onAccountsChange={handleAccountsChange}
            onSelectAllAccounts={handleSelectAllAccounts}
            onDeselectAllAccounts={handleDeselectAllAccounts}
            onLoadMessagesFullChange={handleLoadMessagesFullChange}
            onMessagePeriodFullChange={handleMessagePeriodFullChange}
            // Данные для передачи
            objectsData={objectsDataForTransfer}
            usersData={usersDataForTransfer}
            geozonesData={geozonesDataForTransfer}
            driversData={driversDataForTransfer}
            accountsData={accountsDataForTransfer}
            retranslationsData={retranslationsDataForTransfer}
          />

          <TransferButtonSection
            transferMode={transferMode}
            disabled={isButtonDisabled || isTransferring}
            onClick={() => handleStartTransfer()}
            loading={isTransferring}
            objectsTargetKeys={objectsTargetKeys}
            usersTargetKeys={usersTargetKeys}
            geozonesTargetKeys={geozonesTargetKeys}
            driversTargetKeys={driversTargetKeys}
            retranslationsTargetKeys={retranslationsTargetKeys}
            accountsTargetKeys={accountsTargetKeys}
            text={
              isTransferring ? 'Выполняется перенос...' : 'Запустить перенос'
            }
          />
        </SectionCard>
      </Flex>
    </PageLayout>
  )
}
